import firebase from "firebase/compat";
import "firebase/firestore";
import { has, hasPath } from "ramda";
import { CreateParams, DataProvider, GetListParams, UpdateManyParams, UpdateParams } from "react-admin";
import { FirebaseAuthProvider, FirebaseDataProvider } from "react-admin-firebase";
import ReduxSagaFirebase from "redux-saga-firebase";

import envConfig from "../init/config";
import { setPaginationDirection } from "../redux/reducers/pagination";
import { store } from "../root/ReduxConfig";
import { PaginationDirection } from "../types/pagination.types";
import { DataProviderKey, DataProviderParams } from "../types/reactAdmin.types";
import { prepareGetListParams } from "../utils/collectionQuery";
import { applyCreateTimestamp, applyUpdateTimestamp } from "../utils/timestamp";
import { requestOverrides, resourceNameOverrides } from "./resources";
import { resetWebViewUserToken } from "../shared/BaseNotificationList";
import firebaseConfig from "../init/config";

const firebaseApp = firebase.initializeApp(envConfig);

const options = {
  app: firebaseApp,
  disableMeta: true,
  dontAddIdFieldToDoc: true,
  logging: true,
  firestoreCostsLogger: {
    enabled: true,
  },
};
const firebaseAuthProvider = FirebaseAuthProvider({}, options);

const dataProviderProxy = (key: DataProviderKey, resource: string, params: DataProviderParams) => {
  const { getList, ...restKeys } = DataProviderKey;

  if (has(key, restKeys) && has(resource, resourceNameOverrides)) {
    resource = resourceNameOverrides[resource] as string;
  }

  /**
   * apply timestamp for update and create query
   */
  switch (key) {
    case DataProviderKey.create:
      (params as CreateParams).data = applyCreateTimestamp((params as CreateParams).data);
      break;
    case DataProviderKey.update:
    case DataProviderKey.updateMany:
      (params as UpdateParams | UpdateManyParams).data = applyUpdateTimestamp(
        (params as UpdateParams | UpdateManyParams).data
      );
      break;
  }

  let response;

  switch (key) {
    case DataProviderKey.getList:
      response = requestOverrides[resource][key](prepareGetListParams(resource, params as GetListParams));
      (store.dispatch as any)(
        setPaginationDirection({
          resource,
          direction: PaginationDirection.current,
        })
      );
      break;
    default:
      if (has(resource, requestOverrides) && hasPath([resource, key], requestOverrides)) {
        response = (requestOverrides as any)[resource][key](resource, params);
      } else {
        /**
         * todo - revisit to see where this happened!!
         * Somehow 'snapshot' prop get injected with the data when update query is called
         * so it causes a maximum call stack exceed error, it should be deleted
         * */
        if ((params as any).data && (params as any).data.snapshot) {
          delete (params as any).data.snapshot;
        }
        response = dataProvider[key](resource, params as any);
      }
  }
  return response;
};

export const dataProvider = FirebaseDataProvider({}, options);
export const authProvider = {
  ...firebaseAuthProvider,
  checkError: (error: any) => {
    if (error.status === 403) {
      error.redirectTo = "/";
      return Promise.resolve(error);
    }
    return firebaseAuthProvider.checkError(error);
  },
  logout: async () => {
    try {
      const data = store.getState();
      if (data.user.webviewFcmToken) await resetWebViewUserToken(data.user);
      await firebaseApp.auth().signOut();
    } catch (ex: any) {
      console.log(ex);
    }
  },
};

export const firestore = firebase.firestore();
export const storageRef = firebase.storage().ref();
export const videosStorageRef = firebase.storage().refFromURL(firebaseConfig.videosBucket!);
export const rsf = new ReduxSagaFirebase(firebaseApp);

export const customDataProvider: DataProvider = Object.values(DataProviderKey).reduce((acc, key) => {
  return {
    ...acc,
    [key]: (resource: string, params: DataProviderParams) => dataProviderProxy(key, resource, params),
  };
}, {} as DataProvider);

export default firebaseApp;
